import React from "react";
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import logo from "../components/assets/logo-pet.png";
import banner from "../components/assets/banner.png";
import VisionMissionValues from "../components/VisionMissionValues";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/MailOutline";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PersonIcon from "@mui/icons-material/Person";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PetsIcon from "@mui/icons-material/Pets";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BuildIcon from "@mui/icons-material/Build";

const AboutUs = () => {
  const metrics = [
    {
      value: "1000 +",
      label: "Digital Pet Profiles",
      icon: <PetsIcon />,
      color: "#B4D479",
    },
    {
      value: "98.5%",
      label: "Profile Access Rate",
      icon: <QrCodeIcon />,
      color: "#B4D479",
    },
    {
      value: "Instant",
      label: "Email Alerts",
      icon: <NotificationsActiveIcon />,
      color: "#B4D479",
    },
    {
      value: "100%",
      label: "QR Reliability",
      icon: <PhotoCameraIcon />,
      color: "#B4D479",
    },
  ];

  const features = [
    {
      title: "Digital Pet Identity",
      description:
        "Create a comprehensive digital profile for your pet with photos, contact details, and essential information.",
      highlight: "Complete Profile",
    },
    {
      title: "Instant QR Scanning",
      description:
        "Anyone can scan your pet's tag with their smartphone camera to instantly access your contact information.",
      highlight: "No App Required",
    },
    {
      title: "Real-time Email Alerts",
      description:
        "Receive instant email notifications with precise time and location data whenever someone scans your pet's QR code.",
      highlight: "GPS Tracking",
    },
    {
      title: "Easy Profile Management",
      description:
        "Update your pet's information, photos, and your contact details anytime through our simple web platform.",
      highlight: "Real-time Updates",
    },
  ];

  return (
    <Box
      sx={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        backgroundColor: "#f5f5dc",
        minHeight: "100vh",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #B4D479 0%, #8FBF47 100%)",
          pt: 4,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <RouterLink to="/" style={{ textDecoration: "none" }}>
              <img
                src={logo}
                alt="That's My Pet"
                style={{
                  height: "200px",
                  width: "auto",
                }}
              />
            </RouterLink>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.75rem" },
                fontWeight: 700,
                color: "#353634",
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              Give Your Pet a Digital Identity
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.125rem", md: "1.25rem" },
                fontWeight: 400,
                color: "#353634",
                lineHeight: 1.6,
                maxWidth: "650px",
                mx: "auto",
                mb: 4,
              }}
            >
              Smart QR pet tags with instant email alerts. Get notified with
              exact time and location whenever someone finds your pet.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Link
                href="/order"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#B4D479",
                  backgroundColor: "#353634",
                  borderRadius: "8px",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#2a2b29",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Get Your Tag Now!
              </Link>

              <Link
                href="#features"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#353634",
                  backgroundColor: "transparent",
                  border: "2px solid #353634",
                  borderRadius: "8px",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#353634",
                    color: "#B4D479",
                  },
                }}
              >
                How It Works
              </Link>
            </Box>

            {/* Social Links */}
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 6 }}
            >
              <Link
                href="https://wa.me/+96176656993"
                target="_blank"
                sx={{
                  color: "#353634",
                  "&:hover": { color: "#25D366" },
                  transition: "color 0.2s ease",
                }}
              >
                <WhatsAppIcon fontSize="large" />
              </Link>
              <Link
                href="https://www.instagram.com/thatsmypet.lb"
                target="_blank"
                sx={{
                  color: "#353634",
                  "&:hover": { color: "#E4405F" },
                  transition: "color 0.2s ease",
                }}
              >
                <InstagramIcon fontSize="large" />
              </Link>
              <Link
                href="mailto:sales@thatsmypet.org"
                target="_blank"
                sx={{
                  color: "#353634",
                  "&:hover": { color: "#0f172a" },
                  transition: "color 0.2s ease",
                }}
              >
                <EmailIcon fontSize="large" />
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Metrics Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {metrics.map((metric, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  p: 3,
                  textAlign: "center",
                  backgroundColor: "white",
                  border: "2px solid #B4D479",
                  borderRadius: "12px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <Box sx={{ color: metric.color, mb: 2, fontSize: "2rem" }}>
                  {metric.icon}
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "2.25rem",
                    fontWeight: 700,
                    color: "#353634",
                    mb: 1,
                  }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#353634",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {metric.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Story Section */}
      <Box sx={{ backgroundColor: "white", py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Our Story"
                sx={{
                  backgroundColor: "#B4D479",
                  color: "#353634",
                  fontWeight: 600,
                  mb: 3,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#353634",
                  lineHeight: 1.2,
                  mb: 4,
                }}
              >
                Every pet deserves a digital identity
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#353634",
                  lineHeight: 1.7,
                  fontSize: "1.125rem",
                  mb: 3,
                }}
              >
                Founded in 2024 when we realized that traditional pet tags
                weren't enough. A simple metal tag with a phone number couldn't
                tell the full story of who your pet is.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#353634",
                  lineHeight: 1.7,
                  fontSize: "1.125rem",
                }}
              >
                Today, we've created Lebanon's most comprehensive pet
                identification system. Each QR tag unlocks a complete digital
                profile and sends you instant notifications with location data
                when scanned.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "3px solid #B4D479",
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                }}
              >
                <img
                  src={banner}
                  alt="Digital pet profiles in action"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }} id="features">
        <Box textAlign="center" mb={10}>
          <Chip
            label="How It Works"
            sx={{
              backgroundColor: "#B4D479",
              color: "#353634",
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: "2.25rem",
              fontWeight: 700,
              color: "#353634",
              lineHeight: 1.2,
              mb: 4,
            }}
          >
            Simple setup, instant results
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  p: 4,
                  backgroundColor: "white",
                  border: "2px solid #B4D479",
                  borderRadius: "12px",
                  height: "auto",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: "#353634",
                      mr: 2,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Chip
                    label={feature.highlight}
                    size="small"
                    sx={{
                      backgroundColor: "#B4D479",
                      color: "#353634",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Process Steps */}
      <Box sx={{ backgroundColor: "white", py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "2.25rem",
                fontWeight: 700,
                color: "#353634",
                lineHeight: 1.2,
                mb: 4,
              }}
            >
              From setup to reunion in 3 steps
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: "#353634",
                      fontWeight: "bold",
                      fontSize: "2rem",
                    }}
                  >
                    1
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "#353634",
                    mb: 2,
                  }}
                >
                  Create Your Account
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.6,
                  }}
                >
                  Sign up and create a detailed profile for your pet. Add their
                  photo, your contact information, and location details via
                  Google Maps.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: "#353634",
                      fontWeight: "bold",
                      fontSize: "2rem",
                    }}
                  >
                    2
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "#353634",
                    mb: 2,
                  }}
                >
                  Attach Your Tag
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.6,
                  }}
                >
                  Attach the QR tag to your pet's collar. Each tag is linked to
                  your pet's unique digital profile and ready to scan.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: "#353634",
                      fontWeight: "bold",
                      fontSize: "2rem",
                    }}
                  >
                    3
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "#353634",
                    mb: 2,
                  }}
                >
                  Get Instant Alerts
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.6,
                  }}
                >
                  When someone scans your pet's QR code, you receive an
                  immediate email with the exact time and location of the scan.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision - Improved Design with Better Spacing */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f5f5dc 0%, #e8e2c2 100%)",
          py: 12,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={10}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#353634",
                lineHeight: 1.2,
                mb: 4,
              }}
            >
              What Drives Us Forward
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.2rem",
                color: "#353634",
                maxWidth: "600px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              Our commitment to pet safety and family peace of mind
            </Typography>
          </Box>

          <Grid container spacing={8}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  backgroundColor: "white",
                  borderRadius: "20px",
                  border: "3px solid #B4D479",
                  height: "auto",
                  textAlign: "center",
                  position: "relative",
                  overflow: "visible",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "-60px auto 40px auto",
                    border: "4px solid white",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <VisibilityIcon
                    sx={{ fontSize: "2.5rem", color: "#353634" }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "#353634",
                    mb: 4,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                  }}
                >
                  To give every pet a comprehensive digital identity that
                  ensures quick, reliable contact between finders and families
                  when it matters most.
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  backgroundColor: "white",
                  borderRadius: "20px",
                  border: "3px solid #B4D479",
                  height: "auto",
                  textAlign: "center",
                  position: "relative",
                  overflow: "visible",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "-60px auto 40px auto",
                    border: "4px solid white",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <LocationOnIcon
                    sx={{ fontSize: "2.5rem", color: "#353634" }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "#353634",
                    mb: 4,
                  }}
                >
                  Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                  }}
                >
                  A world where every pet carries their complete story and
                  contact information, making lost pets a thing of the past.
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  backgroundColor: "white",
                  borderRadius: "20px",
                  border: "3px solid #B4D479",
                  height: "auto",
                  textAlign: "center",
                  position: "relative",
                  overflow: "visible",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#B4D479",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "-60px auto 40px auto",
                    border: "4px solid white",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <BuildIcon sx={{ fontSize: "2.5rem", color: "#353634" }} />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "#353634",
                    mb: 4,
                  }}
                >
                  Our Technology
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#353634",
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                  }}
                >
                  QR code technology that works with any smartphone camera,
                  connected to secure cloud profiles with instant email
                  notifications.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #353634 0%, #2a2b29 100%)",
          py: 8,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontSize: "2.25rem",
              fontWeight: 700,
              color: "#B4D479",
              mb: 3,
            }}
          >
            Ready to give your pet a digital identity?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#f5f5dc",
              fontSize: "1.125rem",
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            Join thousands of pet families who trust That's My Pet for reliable,
            instant pet identification with location tracking.
          </Typography>
          <Link
            href="/order"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 6,
              py: 2,
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "#353634",
              backgroundColor: "#B4D479",
              borderRadius: "8px",
              textDecoration: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#a8c766",
                transform: "translateY(-1px)",
              },
            }}
          >
            Order yours now!
          </Link>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: "#353634",
          borderTop: "3px solid #B4D479",
          py: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{
              color: "#B4D479",
              fontSize: "0.875rem",
            }}
          >
            © {new Date().getFullYear()} That's My Pet. All rights reserved. •
            Digital pet identities
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;
